import { useEffect, useMemo, useRef, useState } from 'react';
import { CATEGORY_LABEL, type RoundSnapshot } from '@hypevault/draw-guess';
import { DEFAULT_GAME_SOUND_CONFIG } from '@hypevault/shared-types';
import { gameSounds } from '../lib/gameSounds';

/**
 * Draw & Guess — 1920×1080 overlay.
 *
 * drawing phase  : Replicate-generated image behind an 8×8 tile mask that
 *                  un-covers progressively over the round duration. Right
 *                  panel shows category, letter-count hint, timer, recent
 *                  guesses, and winners as they claim.
 * reveal phase   : Tiles wipe instantly, word slams in, top 3 winners
 *                  shown for ~8s before the widget goes idle (snapshot
 *                  cleared by runner).
 */

interface Fields {
  position?: 'fullscreen';
  scale?: number;
  opacity?: number;
}

interface Props {
  streamerId: string;
  snapshot: RoundSnapshot | null;
  fields: Fields;
}

const ANIMATIONS = `
@keyframes dg-fadeIn { from { opacity: 0 } to { opacity: 1 } }
@keyframes dg-tileFlip {
  0% { transform: scale(1); opacity: 1 }
  60% { transform: scale(1.1) rotate(8deg); opacity: 0.6 }
  100% { transform: scale(0) rotate(20deg); opacity: 0 }
}
@keyframes dg-slam {
  0% { transform: scale(0.4); opacity: 0; filter: blur(8px) }
  60% { transform: scale(1.1); opacity: 1; filter: blur(0) }
  100% { transform: scale(1); opacity: 1; filter: blur(0) }
}
@keyframes dg-shimmer {
  0% { background-position: -200px 0 }
  100% { background-position: 200px 0 }
}
@keyframes dg-winnerIn {
  0% { transform: translateX(40px); opacity: 0 }
  100% { transform: translateX(0); opacity: 1 }
}
@keyframes dg-guessIn {
  0% { transform: translateY(10px); opacity: 0 }
  100% { transform: translateY(0); opacity: 1 }
}
@keyframes dg-pulse {
  0%, 100% { transform: scale(1) }
  50% { transform: scale(1.05) }
}
`;

const PALETTE = {
  bg: '#0b1020',
  panel: 'rgba(15, 23, 42, 0.85)',
  panelBorder: 'rgba(148, 163, 184, 0.18)',
  tile: '#1e293b',
  tileBorder: 'rgba(56, 189, 248, 0.18)',
  accent: '#f655a6',
  accentGlow: 'rgba(246, 85, 166, 0.4)',
  text: '#f8fafc',
  textDim: 'rgba(248, 250, 252, 0.65)',
  gold: '#facc15',
  green: '#22c55e',
};

const GRID_COLS = 8;
const GRID_ROWS = 8;

export function DrawGuessWidget({ streamerId, snapshot, fields }: Props) {
  const initial = useRef<RoundSnapshot | null>(snapshot);
  const [latest, setLatest] = useState<RoundSnapshot | null>(snapshot);
  const prevRef = useRef<RoundSnapshot | null>(snapshot);

  // Bootstrap from /api/draw-guess/snapshot on first mount so the widget
  // paints immediately rather than waiting for the next pubsub event.
  useEffect(() => {
    if (initial.current) return;
    const tokenMatch = window.location.search.match(/token=([^&]+)/);
    if (!tokenMatch) return;
    const ac = new AbortController();
    fetch(`/api/draw-guess/snapshot?token=${tokenMatch[1]}`, { signal: ac.signal })
      .then((res) => (res.ok ? res.json() : null))
      .then(
        (json: { snapshot?: RoundSnapshot; sound?: typeof DEFAULT_GAME_SOUND_CONFIG } | null) => {
          if (json?.snapshot) setLatest(json.snapshot);
          gameSounds.configure(json?.sound ?? DEFAULT_GAME_SOUND_CONFIG);
        },
      )
      .catch(() => undefined);
    return () => ac.abort();
  }, []);

  useEffect(() => {
    if (snapshot) setLatest(snapshot);
  }, [snapshot]);
  useEffect(() => {
    const prev = prevRef.current;
    prevRef.current = latest;
    if (!latest) return;
    if (!prev || prev.roundId !== latest.roundId) {
      if (latest.phase === 'drawing') void gameSounds.play('game_start');
      return;
    }
    if (prev.revealedTiles < latest.revealedTiles) {
      void gameSounds.play('reveal');
    }
    if (prev.winners.length < latest.winners.length) {
      void gameSounds.play('correct');
      void gameSounds.play('winner');
    }
    if (prev.phase !== latest.phase && latest.phase === 'reveal') {
      void gameSounds.play('celebration');
      if (latest.winners.length > 0) void gameSounds.play('gems_awarded');
    }
  }, [latest]);
  void streamerId;

  const opacity = typeof fields.opacity === 'number' ? fields.opacity : 1;
  const scale = typeof fields.scale === 'number' ? fields.scale : 1;

  if (!latest) return null;
  return <Stage snapshot={latest} opacity={opacity} scale={scale} />;
}

function Stage({
  snapshot,
  opacity,
  scale,
}: {
  snapshot: RoundSnapshot;
  opacity: number;
  scale: number;
}) {
  const isReveal = snapshot.phase === 'reveal';
  // Outer wrapper fills the OBS viewport with the bg and centers the design.
  // The inner box is the fixed 1920x1080 canvas, scaled via CSS transform so
  // it fits *any* OBS source size (1920x1080, 1280x720, etc.) while keeping
  // all internal pixel sizes intact.
  const fitScale = `min(calc(100vw / 1920), calc(100vh / 1080))`;
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        background: PALETTE.bg,
        opacity,
        animation: 'dg-fadeIn 300ms ease',
      }}
    >
      <div
        style={{
          position: 'relative',
          width: 1920,
          height: 1080,
          flexShrink: 0,
          color: PALETTE.text,
          fontFamily: 'Inter, system-ui, sans-serif',
          transform: `scale(calc(${fitScale} * ${scale}))`,
          transformOrigin: 'center center',
        }}
      >
        <style>{ANIMATIONS}</style>

        {/* Ambient gradient backing */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(circle at 20% 30%, rgba(246,85,166,0.12), transparent 55%), radial-gradient(circle at 80% 70%, rgba(56,189,248,0.10), transparent 55%)',
            pointerEvents: 'none',
          }}
        />

        <div
          style={{
            position: 'absolute',
            inset: 60,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 40,
          }}
        >
          <ImageCanvas snapshot={snapshot} isReveal={isReveal} />
          <SidePanel snapshot={snapshot} isReveal={isReveal} />
        </div>

        {isReveal && <WinnerSplash snapshot={snapshot} />}
      </div>
    </div>
  );
}

function ImageCanvas({ snapshot, isReveal }: { snapshot: RoundSnapshot; isReveal: boolean }) {
  // Render the tile grid by index, but consult the tile-order permutation so
  // the first N indices in the order are the ones to flip away first.
  const flippedSet = useMemo(() => {
    const order = snapshot.tileOrder ?? [];
    return new Set(order.slice(0, snapshot.revealedTiles));
  }, [snapshot.tileOrder, snapshot.revealedTiles]);

  return (
    <div
      style={{
        position: 'relative',
        background: '#fff',
        borderRadius: 24,
        overflow: 'hidden',
        border: `1px solid ${PALETTE.panelBorder}`,
        boxShadow: '0 30px 80px rgba(0,0,0,0.45)',
        width: 960,
        height: 960,
        margin: 'auto',
      }}
    >
      {snapshot.imageUrl && (
        <img
          src={snapshot.imageUrl}
          alt=""
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            background: '#fff',
          }}
        />
      )}
      {/* Tile mask */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'grid',
          gridTemplateColumns: `repeat(${GRID_COLS}, 1fr)`,
          gridTemplateRows: `repeat(${GRID_ROWS}, 1fr)`,
          pointerEvents: 'none',
        }}
      >
        {Array.from({ length: GRID_COLS * GRID_ROWS }, (_, i) => {
          const flipped = flippedSet.has(i) || isReveal;
          return (
            <div
              key={i}
              style={{
                background: flipped
                  ? 'transparent'
                  : `linear-gradient(135deg, ${PALETTE.tile}, ${PALETTE.bg})`,
                border: flipped ? 'none' : `1px solid ${PALETTE.tileBorder}`,
                animation: flipped ? 'dg-tileFlip 350ms ease forwards' : undefined,
              }}
            />
          );
        })}
      </div>
    </div>
  );
}

function SidePanel({ snapshot, isReveal }: { snapshot: RoundSnapshot; isReveal: boolean }) {
  // Timer ticks down based on snapshot timeRemainingMs at publish time. We
  // can refine with a local RAF tick, but updates land every tile so the
  // delta stays small in practice.
  const secs = Math.max(0, Math.ceil(snapshot.timeRemainingMs / 1000));
  const pct = Math.max(0, Math.min(1, snapshot.timeRemainingMs / snapshot.durationMs));
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        background: PALETTE.panel,
        backdropFilter: 'blur(12px)',
        border: `1px solid ${PALETTE.panelBorder}`,
        borderRadius: 24,
        padding: 24,
        width: 460,
        flexShrink: 0,
        alignSelf: 'stretch',
      }}
    >
      <div
        style={{
          fontSize: 18,
          fontWeight: 600,
          letterSpacing: 2,
          color: PALETTE.textDim,
          textTransform: 'uppercase',
        }}
      >
        {isReveal ? 'Reveal' : 'Draw & Guess'}
      </div>

      {snapshot.category && (
        <div
          style={{
            display: 'inline-flex',
            alignSelf: 'flex-start',
            padding: '6px 14px',
            borderRadius: 999,
            background: `linear-gradient(90deg, ${PALETTE.accent}, #a855f7)`,
            color: '#fff',
            fontWeight: 700,
            fontSize: 14,
            letterSpacing: 1,
            textTransform: 'uppercase',
          }}
        >
          {CATEGORY_LABEL[snapshot.category]}
        </div>
      )}

      {snapshot.maskedWord && !isReveal && (
        <div
          style={{
            fontFamily: 'ui-monospace, "JetBrains Mono", monospace',
            fontSize: 44,
            letterSpacing: 6,
            color: PALETTE.text,
            textAlign: 'center',
            padding: '12px 0',
          }}
        >
          {snapshot.maskedWord}
        </div>
      )}

      {isReveal && snapshot.word && (
        <div
          style={{
            fontSize: 52,
            fontWeight: 900,
            letterSpacing: 2,
            color: PALETTE.gold,
            textAlign: 'center',
            padding: '12px 0',
            textShadow: `0 0 40px ${PALETTE.gold}`,
            animation: 'dg-slam 500ms ease forwards',
            textTransform: 'uppercase',
          }}
        >
          {snapshot.word}
        </div>
      )}

      {!isReveal && (
        <>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 28,
              fontWeight: 700,
            }}
          >
            <span
              style={{
                color: PALETTE.textDim,
                fontSize: 14,
                letterSpacing: 2,
                textTransform: 'uppercase',
              }}
            >
              Time
            </span>
            <span style={{ color: secs <= 10 ? PALETTE.accent : PALETTE.text }}>{secs}s</span>
          </div>
          <div
            style={{
              height: 6,
              background: 'rgba(255,255,255,0.08)',
              borderRadius: 999,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${pct * 100}%`,
                height: '100%',
                background: `linear-gradient(90deg, ${PALETTE.accent}, ${PALETTE.gold})`,
                transition: 'width 0.6s linear',
              }}
            />
          </div>
        </>
      )}

      <div
        style={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          maxHeight: 320,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            fontSize: 14,
            letterSpacing: 2,
            color: PALETTE.textDim,
            textTransform: 'uppercase',
          }}
        >
          Recent Guesses
        </div>
        {snapshot.recentGuesses
          .slice(-8)
          .reverse()
          .map((g, i) => (
            <div
              key={`${g.timestamp}-${i}`}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: 18,
                color: g.isCorrect ? PALETTE.green : PALETTE.text,
                animation: 'dg-guessIn 200ms ease forwards',
                opacity: 1 - i * 0.08,
              }}
            >
              <span style={{ fontWeight: g.isCorrect ? 800 : 500 }}>{g.username}</span>
              <span
                style={{
                  color: g.isCorrect ? PALETTE.green : PALETTE.textDim,
                  fontFamily: 'ui-monospace, "JetBrains Mono", monospace',
                  maxWidth: 240,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {g.isCorrect ? '✓' : g.message}
              </span>
            </div>
          ))}
      </div>

      {snapshot.winners.length > 0 && (
        <div
          style={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            borderTop: `1px solid ${PALETTE.panelBorder}`,
            paddingTop: 16,
          }}
        >
          <div
            style={{
              fontSize: 14,
              letterSpacing: 2,
              color: PALETTE.textDim,
              textTransform: 'uppercase',
            }}
          >
            Winners
          </div>
          {snapshot.winners.map((w) => (
            <div
              key={`${w.placement}-${w.username}`}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '8px 12px',
                background: 'rgba(250,204,21,0.08)',
                borderRadius: 12,
                border: `1px solid ${PALETTE.gold}`,
                animation: 'dg-winnerIn 350ms ease forwards',
              }}
            >
              <span style={{ fontWeight: 700, fontSize: 18 }}>
                <span style={{ color: PALETTE.gold, marginRight: 8 }}>#{w.placement}</span>
                {w.username}
              </span>
              <span style={{ color: PALETTE.gold, fontWeight: 800 }}>+{w.gemsAwarded} 💎</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function WinnerSplash({ snapshot }: { snapshot: RoundSnapshot }) {
  if (snapshot.winners.length === 0) {
    // Timer expired with no winners — quieter splash.
    return (
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'center',
          pointerEvents: 'none',
          paddingBottom: 80,
        }}
      >
        <div
          style={{
            padding: '14px 36px',
            background: 'rgba(15, 23, 42, 0.9)',
            borderRadius: 999,
            border: `1px solid ${PALETTE.panelBorder}`,
            color: PALETTE.textDim,
            fontWeight: 700,
            letterSpacing: 2,
            textTransform: 'uppercase',
            fontSize: 18,
            animation: 'dg-fadeIn 400ms ease',
          }}
        >
          No winners — better luck next round
        </div>
      </div>
    );
  }
  return null;
}
